import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Page-quality signalling for AdSense / Search compliance.
 *
 * Google's "Low value content" policy is violated in two distinct ways, and we
 * have to answer both:
 *
 *   1. Thin pages get indexed  → fix with `noindex, follow`.
 *   2. Ads get served *on* thin pages → fix by not rendering the ad slot at all.
 *
 * A page declares itself thin by rendering <ThinPage />. The Layout reads the
 * same context and skips its footer ad unit. Marking is reference-counted so
 * nested/conditional callers can't leave the flag stuck on.
 */

interface PageQualityValue {
  isThin: boolean;
  markThin: () => () => void;
}

const PageQualityContext = createContext<PageQualityValue | null>(null);

export function PageQualityProvider({ children }: { children: ReactNode }) {
  const [thinCount, setThinCount] = useState(0);

  const value = useMemo<PageQualityValue>(
    () => ({
      isThin: thinCount > 0,
      markThin: () => {
        setThinCount((n) => n + 1);
        return () => setThinCount((n) => Math.max(0, n - 1));
      },
    }),
    [thinCount]
  );

  return <PageQualityContext.Provider value={value}>{children}</PageQualityContext.Provider>;
}

export function usePageQuality(): PageQualityValue {
  // Fall back to a no-op so components stay usable outside the Layout tree
  // (auth pages, tests, Storybook) instead of throwing.
  return (
    useContext(PageQualityContext) ?? {
      isThin: false,
      markThin: () => () => {},
    }
  );
}

/**
 * Pause AdSense **Auto Ads** for as long as a thin page is mounted.
 *
 * Withholding our own <AdUnit> slots is only half the job: Auto Ads is an
 * account-level setting that injects ads straight into <body>, bypassing every
 * component boundary in this app. `pauseAdRequests` is Google's documented
 * switch for exactly this — set it before the tag processes a page view and no
 * new ad requests are issued.
 *
 * Reference-counted so overlapping thin pages can't unpause each other, and
 * only ever restored to 0 when the last one unmounts.
 *
 * This is defence in depth, NOT the primary control: the tag may already have
 * requested ads before React mounts on a cold load. The authoritative fix is
 * the URL exclusion list in the AdSense console — see
 * docs/adsense-auto-ads.md. Prerendered thin routes additionally ship without
 * the AdSense script at all (scripts/prerender.mjs).
 */
let autoAdsPauseDepth = 0;

function pauseAutoAds(): () => void {
  if (typeof window === 'undefined') return () => {};

  autoAdsPauseDepth += 1;
  const queue = (window.adsbygoogle = window.adsbygoogle || []);
  (queue as unknown as { pauseAdRequests?: number }).pauseAdRequests = 1;

  return () => {
    autoAdsPauseDepth = Math.max(0, autoAdsPauseDepth - 1);
    if (autoAdsPauseDepth === 0) {
      (queue as unknown as { pauseAdRequests?: number }).pauseAdRequests = 0;
    }
  };
}

/** Heuristics for "would a reviewer call this page thin?" */
export const thinPageRules = {
  /** A discussion with no substantive replies is a stub, not an article. */
  emptyThread: (replyCount: number, bodyLength: number) => replyCount === 0 && bodyLength < 500,
  /** Search/filter permutations are infinite and duplicative — never index them. */
  searchResults: () => true,
  /** A profile with no bio and no activity has nothing to rank for. */
  stubProfile: (bio: string | null | undefined, activityCount: number) =>
    (bio ?? '').trim().length < 120 && activityCount < 3,
  /** Any list view that came back empty. */
  emptyCollection: (itemCount: number) => itemCount === 0,
};

interface ThinPageProps {
  /**
   * When false the page is treated as normal — lets callers write
   * `<ThinPage when={thinPageRules.emptyThread(replies.length, body.length)} />`
   * without branching at the call site.
   */
  when?: boolean;
  /** Dev-only note explaining why this page is thin. Not rendered. */
  reason?: string;
}

/**
 * Marks the current page as low-value: emits `noindex, follow` and tells the
 * Layout to withhold ad units.
 *
 * The robots tag here intentionally duplicates what <SEO noindex> can emit —
 * react-helmet-async resolves duplicate `name="robots"` tags by keeping the
 * last one mounted, and pages that use both are already agreeing.
 */
export function ThinPage({ when = true, reason }: ThinPageProps) {
  const { markThin } = usePageQuality();

  useEffect(() => {
    if (!when) return;
    if (import.meta.env.DEV && reason) {
      console.debug(`[PageQuality] noindex: ${reason}`);
    }
    const releaseThin = markThin();
    const resumeAutoAds = pauseAutoAds();
    return () => {
      releaseThin();
      resumeAutoAds();
    };
    // markThin identity changes with the count; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when, reason]);

  if (!when) return null;

  return (
    <Helmet>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
  );
}
